import React, { useState, useEffect } from 'react';
import { FaArrowLeft, FaArrowRight, FaTimes, FaRegSave, FaMinus, FaPlus } from 'react-icons/fa';
import axios from '../../../../config/axios';
import Swal from 'sweetalert2';

const MenusModal = ({ tipo, onClose }) => {
    const [allMenus, setAllMenus] = useState([]);
    const [associados, setAssociados] = useState([]);

    const naoAssociados = React.useMemo(() => {
        const assocIds = new Set(associados.map(m => m.id));
        return allMenus.filter(m => !assocIds.has(m.id));
    }, [allMenus, associados]);

    const [selectedLeft, setSelectedLeft] = useState(null);
    const [selectedRight, setSelectedRight] = useState(null);

    const moveRight = () => {
        if (!selectedLeft) return;
        const menu = allMenus.find(m => m.id === selectedLeft);
        if (!menu) return;
        
        const menuComSubmenus = {
            ...menu,
            submenus: menu.submenus ? menu.submenus.map(sub => ({
                ...sub,
                ativo: true
            })) : []
        };
        
        setAssociados(prev => [...prev, menuComSubmenus]);
        setSelectedLeft(null);
    };

    const moveLeft = () => {
        if (!selectedRight) return;
        const menuToRemove = associados.find(m => m.id === selectedRight);
        if (menuToRemove) {
            setAssociados(prev => prev.filter(m => m.id !== selectedRight));
        }
        setSelectedRight(null);
    };


    const toggleSubmenu = (menuId, submenuId) => {
        setAssociados(prev => {
            const updated = prev.map(m => m.id === menuId ? {
                ...m,
                submenus: (m.submenus || []).map(sm => sm.id === submenuId ? { ...sm, ativo: !sm.ativo } : sm)
            } : m);
            
            return updated.filter(menu => {
                if (menu.submenus && menu.submenus.length > 0) {
                    const hasActiveSubmenus = menu.submenus.some(sm => sm.ativo !== false);
                    return hasActiveSubmenus;
                }
                return true;
            });
        });
    };

    const fetchAllMenus = async () => {
        try {
            const response = await axios.get('/menus/hierarquicos');
            setAllMenus(response.data);
            return response.data;
        } catch (error) {
            Swal.fire('Erro!', 'Erro ao carregar menus', 'error');
            return [];
        }
    };

    const fetchMenusAssociados = async (todosMenus = null) => {
        if (!tipo || !tipo.id) return;

        Swal.fire({
            title: 'Carregando...',
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => Swal.showLoading(),
        });
        
        try {
            const response = await axios.get(`/tipos-de-acesso/${tipo.id}/menus`);
            const menusAssociados = response.data;
            
            const menusDisponiveis = todosMenus || allMenus;
            
            const submenusAssociadosIds = new Set();
            menusAssociados.forEach(menu => {
                if (menu.submenus) {
                    menu.submenus.forEach(sub => submenusAssociadosIds.add(sub.id));
                }
            });
            
            const menusCompletos = menusAssociados.map(menuAssociado => {
                const menuCompleto = menusDisponiveis.find(m => m.id === menuAssociado.id);
                
                if (!menuCompleto) return menuAssociado;
                
                if (menuCompleto.submenus && menuCompleto.submenus.length > 0) {
                    return {
                        ...menuCompleto,
                        submenus: menuCompleto.submenus.map(submenu => ({
                            ...submenu,
                            ativo: submenusAssociadosIds.has(submenu.id)
                        }))
                    };
                }
                
                return menuCompleto;
            });
            
            setAssociados(menusCompletos);
        } catch (error) {
            Swal.fire('Erro!', 'Erro ao carregar menus do tipo de acesso', 'error');
        } finally {
            Swal.close();
        }
    };

    const handleSave = async () => {
        if (!tipo || !tipo.id) return;
        
        Swal.fire({
            title: 'Carregando...',
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => Swal.showLoading(),
        });
        
        try {
            const menuIds = [];
            
            associados.forEach(menu => {
                menuIds.push(menu.id);
                
                if (menu.submenus && menu.submenus.length > 0) {
                    menu.submenus.forEach(submenu => {
                        if (submenu.ativo !== false) {
                            menuIds.push(submenu.id);
                        }
                    });
                }
            });
            
            Swal.close();
            await axios.put(`/tipos-de-acesso/${tipo.id}/menus`, { menuIds });
            Swal.fire('Sucesso!', 'Menus do tipo de acesso atualizados com sucesso', 'success');
            onClose();
        } catch (error) {
            Swal.close();
            Swal.fire('Erro!', 'Erro ao salvar menus do tipo de acesso', 'error');
        }
    };

    useEffect(() => {
        const loadData = async () => {
            const todosMenus = await fetchAllMenus();
            await fetchMenusAssociados(todosMenus);
        };
        
        loadData();
    }, [tipo]);

    return (
        <div className="modal d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-xl modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h4 className="fw-bold mt-2">Menus do Tipo: {tipo?.nome}</h4>
                        <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        <div className="row g-3">
                            <div className="col-md-5">
                                <div className="card h-100">
                                    <div className="card-header d-flex align-items-center justify-content-between">
                                        <span>Não Associados</span>
                                    </div>
                                    <div className="card-body" style={{ maxHeight: 420, overflow: 'auto' }}>
                                        <ul className="list-group">
                                            {naoAssociados.map(menu => (
                                                <li
                                                    key={menu.id}
                                                    className={`list-group-item ${selectedLeft === menu.id ? 'active' : ''}`}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => setSelectedLeft(prev => prev === menu.id ? null : menu.id)}
                                                >
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <span>{menu.nome}</span>
                                                    </div>
                                                    {menu.submenus?.length > 0 && (
                                                        <ul className="mt-2 mb-0 list-unstyled" style={{ paddingLeft: 18 }}>
                                                            {menu.submenus.map((sm, idx) => (
                                                                <li key={sm.id} className={`d-flex justify-content-between align-items-center ${idx > 0 ? 'mt-2' : ''}`}>
                                                                    <span className={selectedLeft === menu.id ? 'text-white' : 'text-muted'}>{sm.nome}</span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            <div className="col-md-2 d-flex flex-column align-items-center justify-content-center">
                                <button className="btn btn-outline-primary mb-2 d-flex align-items-center" onClick={moveRight}>
                                    <FaArrowRight className="me-2" /> Associar
                                </button>
                                <button className="btn btn-outline-secondary d-flex align-items-center" onClick={moveLeft}>
                                    <FaArrowLeft className="me-2" /> Remover
                                </button>
                            </div>

                            <div className="col-md-5">
                                <div className="card h-100">
                                    <div className="card-header d-flex align-items-center justify-content-between">
                                        <span>Associados</span>
                                    </div>
                                    <div className="card-body" style={{ maxHeight: 420, overflow: 'auto' }}>
                                        <ul className="list-group">
                                            {associados.map(menu => (
                                                <li
                                                    key={menu.id}
                                                    className={`list-group-item ${selectedRight === menu.id ? 'active' : ''}`}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => setSelectedRight(prev => prev === menu.id ? null : menu.id)}
                                                >
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <span>{menu.nome}</span>
                                                    </div>
                                                    {menu.submenus?.length > 0 && (
                                                        <ul className="mt-2 mb-0" style={{ paddingLeft: 18 }}>
                                                            {menu.submenus.map((sm, idx) => (
                                                                <li key={sm.id} className={`d-flex justify-content-between align-items-center ${idx > 0 ? 'mt-2' : ''}`}>
                                                                    <span className={selectedRight === menu.id ? 'text-white' : 'text-muted'}>
                                                                        {sm.nome}
                                                                    </span>
                                                                    <button 
                                                                        className={`btn btn-sm ${sm.ativo ? 'btn-danger' : 'btn-success'}`} 
                                                                        onClick={(e) => { 
                                                                            e.stopPropagation(); 
                                                                            setSelectedRight(menu.id); 
                                                                            toggleSubmenu(menu.id, sm.id); 
                                                                        }}
                                                                        title={sm.ativo ? 'Desabilitar' : 'Habilitar'}
                                                                    >
                                                                        {sm.ativo ? <FaMinus /> : <FaPlus />}
                                                                    </button>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer mt-2">
                        <button className="btn btn-outline-danger" type="button" onClick={onClose}>
                            <FaTimes /> Fechar
                        </button>
                        <button className="btn btn-custom-primary" type="button" onClick={handleSave}>
                            <FaRegSave /> Salvar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MenusModal;


